import { useEffect, useState } from 'react';
import { Bell, BellOff, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { analytics } from '../lib/analytics';

interface ReminderSettings {
  enabled: boolean;
  time: string; // 24-hour format HH:MM
  frequency: 'daily' | 'every_other_day' | 'weekdays_only';
  missedDaysBeforeReminder: number;
}

export function MissedCheckInReminders() {
  const [settings, setSettings] = useState<ReminderSettings>({
    enabled: true,
    time: '20:00', // 8 PM default
    frequency: 'daily',
    missedDaysBeforeReminder: 1,
  });
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    // Load saved settings
    const saved = localStorage.getItem('tension_reminder_settings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }

    // Check notification permission
    if ('Notification' in window) {
      // DEFENSIVE: Wrap Notification API access in try-catch
      try {
        setNotificationPermission(Notification.permission);
      } catch (error) {
        console.debug('Notification API error:', error);
        setNotificationPermission('denied');
      }
    }

    // Set up daily check for missed check-ins
    const interval = setInterval(() => {
      checkMissedCheckIns();
    }, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, []);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        toast.success('Notifications enabled!');
        analytics.track('notification_permission_granted');
      } else {
        toast.error('Notification permission denied');
        analytics.track('notification_permission_denied');
      }
    } catch (error) {
      console.error('Notification permission error:', error);
      toast.error('Failed to request notification permission');
    }
  };

  const checkMissedCheckIns = () => {
    if (!settings.enabled) return;

    const lastCheckIn = localStorage.getItem('tension_last_checkin_date');
    if (!lastCheckIn) return;

    const lastDate = new Date(lastCheckIn);
    const now = new Date();
    const daysSinceLastCheckIn = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceLastCheckIn >= settings.missedDaysBeforeReminder) {
      showReminderNotification(daysSinceLastCheckIn);
    }
  };

  const showReminderNotification = (daysMissed: number) => {
    if (notificationPermission !== 'granted') return;

    const messages = [
      `You haven't checked in for ${daysMissed} ${daysMissed === 1 ? 'day' : 'days'}. How are things?`,
      `Missing your check-in! ${daysMissed} ${daysMissed === 1 ? 'day' : 'days'} without tracking.`,
      `Your patterns matter. Check in to keep your insights accurate.`,
      `Building consistency: Don't break your awareness streak!`,
    ];

    const message = messages[Math.floor(Math.random() * messages.length)];

    // DEFENSIVE: Wrap Notification in try-catch to prevent "Illegal constructor" errors
    try {
      new Notification('TENSION Check-In Reminder', {
        body: message,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-96x96.png',
        tag: 'missed-checkin',
        requireInteraction: false,
      });
    } catch (error) {
      console.debug('Notification API error:', error);
    }

    analytics.track('missed_checkin_reminder_shown', { daysMissed });
  };

  const saveSettings = (newSettings: ReminderSettings) => {
    setSettings(newSettings);
    localStorage.setItem('tension_reminder_settings', JSON.stringify(newSettings));
    toast.success('Reminder settings updated');
    analytics.track('reminder_settings_updated', newSettings);
  };

  const scheduleReminder = () => {
    if (!settings.enabled || notificationPermission !== 'granted') return;

    // Calculate next reminder time
    const now = new Date();
    const [hours, minutes] = settings.time.split(':').map(Number);
    const reminderTime = new Date();
    reminderTime.setHours(hours, minutes, 0, 0);

    if (reminderTime <= now) {
      reminderTime.setDate(reminderTime.getDate() + 1);
    }

    const timeUntilReminder = reminderTime.getTime() - now.getTime();

    setTimeout(() => {
      checkMissedCheckIns();
      scheduleReminder(); // Reschedule for next day
    }, timeUntilReminder);
  };

  useEffect(() => {
    if (settings.enabled && notificationPermission === 'granted') {
      scheduleReminder();
    }
  }, [settings.enabled, settings.time, notificationPermission]);

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Check-In Reminders
          </h3>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Get notified when you miss daily check-ins
          </p>
        </div>
        <Switch
          checked={settings.enabled}
          onCheckedChange={(enabled) => saveSettings({ ...settings, enabled })}
          aria-label="Toggle check-in reminders"
        />
      </div>

      {notificationPermission !== 'granted' && settings.enabled && (
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <BellOff className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                Enable browser notifications to receive reminders
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={requestNotificationPermission}
                className="border-amber-300 dark:border-amber-700"
              >
                Enable Notifications
              </Button>
            </div>
          </div>
        </div>
      )}

      {settings.enabled && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reminderTime">Reminder Time</Label>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-neutral-500" />
              <Input
                id="reminderTime"
                type="time"
                value={settings.time}
                onChange={(e) => saveSettings({ ...settings, time: e.target.value })}
                className="flex-1"
                aria-label="Set reminder time"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="frequency">Frequency</Label>
            <Select
              value={settings.frequency}
              onValueChange={(value) =>
                saveSettings({ ...settings, frequency: value as ReminderSettings['frequency'] })
              }
            >
              <SelectTrigger id="frequency" aria-label="Select reminder frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="every_other_day">Every Other Day</SelectItem>
                <SelectItem value="weekdays_only">Weekdays Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="missedDays">Remind me after missing</Label>
            <Select
              value={settings.missedDaysBeforeReminder.toString()}
              onValueChange={(value) =>
                saveSettings({ ...settings, missedDaysBeforeReminder: parseInt(value) })
              }
            >
              <SelectTrigger id="missedDays" aria-label="Select days before reminder">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="2">2 days</SelectItem>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="7">1 week</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              showReminderNotification(1);
              toast.success('Test notification sent!');
            }}
            disabled={notificationPermission !== 'granted'}
            className="w-full"
          >
            Send Test Notification
          </Button>
        </div>
      )}
    </Card>
  );
}

// Simple input component for time (inline to avoid import issues)
function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-950 dark:ring-offset-neutral-950 dark:placeholder:text-neutral-400 dark:focus-visible:ring-neutral-300 ${className}`}
      {...props}
    />
  );
}